import React, { useState, useEffect } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import useApi from '../hooks/useApi';
import SocialPostCard from '../components/dashboard/SocialPostCard';
import TestimonialCard from '../components/dashboard/TestimonialCard';
import { FiEdit } from 'react-icons/fi';

const BrandingPage = () => {
  const { data: apiData, loading, error } = useApi('branding');
  const [brandingData, setBrandingData] = useState(null);
  
  // Update branding data when API data is received
  useEffect(() => {
    if (apiData) {
      setBrandingData(apiData);
    }
  }, [apiData, loading]);
  
  const companyProfile = brandingData?.companyProfile || {};
  const socialFeed = brandingData?.socialFeed || [];
  const testimonials = brandingData?.testimonials || [];
  const engagementMetrics = brandingData?.engagementMetrics || {};

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans">
      <Sidebar />
      <main className="flex-1 p-8">
        <DashboardHeader />
        <div className="mt-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Employer Branding</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-8">
              <CompanyProfilePreview profile={companyProfile} />
              <SocialFeed feed={socialFeed} />
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <EngagementMetrics metrics={engagementMetrics} />
              <Testimonials testimonials={testimonials} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const CompanyProfilePreview = ({ profile }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm relative">
        <img src={profile.banner} alt="Company Banner" className="w-full h-40 object-cover rounded-t-lg" />
        <div className="-mt-12 ml-6">
            <img src={profile.logo} alt="Company Logo" className="w-24 h-24 rounded-full border-4 border-white shadow-md" />
        </div>
        <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900">{profile.name}</h2>
            <p className="text-sm text-gray-600 mt-2 italic">"{profile.mission}"</p>
        </div>
        <button className="absolute top-4 right-4 bg-white/50 p-2 rounded-full hover:bg-white">
            <FiEdit />
        </button>
    </div>
);

const SocialFeed = ({ feed }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Social Feed</h3>
        <div className="space-y-4">
            {feed.map(post => <SocialPostCard key={post.id} post={post} />)}
        </div>
    </div>
);

const EngagementMetrics = ({ metrics }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Engagement Metrics</h3>
        <div className="space-y-4">
            <MetricItem label="Profile Views" value={metrics.profileViews.toLocaleString()} />
            <MetricItem label="Job Views" value={metrics.jobViews.toLocaleString()} />
            <MetricItem label="Profile Conversion Rate" value={`${metrics.conversionRate}%`} />
        </div>
    </div>
);

const MetricItem = ({ label, value }) => (
    <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-lg font-bold text-indigo-600">{value}</p>
    </div>
);

const Testimonials = ({ testimonials }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Employee Testimonials</h3>
        <div className="space-y-4">
            {testimonials.map(t => <TestimonialCard key={t.id} testimonial={t} />)}
        </div>
    </div>
);

export default BrandingPage;
